import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLogin from './components/AdminLogin';
import { createClient } from '@supabase/supabase-js';

interface Registration {
  id: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
  registration_date: string;
  kvkk_accepted_at: string;
  ip_address: string;
}

interface ConnectionLog {
  id: string;
  ip_address: string;
  user_agent: string;
  timestamp: string;
  page_url: string;
}

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'registrations' | 'logs' | 'kvkk'>('registrations');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [connectionLogs, setConnectionLogs] = useState<ConnectionLog[]>([]);
  const [kvkkText, setKvkkText] = useState('');
  const [isEditingKvkk, setIsEditingKvkk] = useState(false);
  const [tempKvkkText, setTempKvkkText] = useState('');

  useEffect(() => {
    // Giriş durumunu kontrol et
    const checkAuth = () => {
      const authStatus = localStorage.getItem('admin_authenticated');
      setIsAuthenticated(authStatus === 'true');
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      const supabase = createClient(supabaseUrl, supabaseKey);

      // Kayıtları yükle
      const { data: registrations, error: regError } = await supabase
        .from('registrations')
        .select('id, name, participation, created_at')
        .order('created_at', { ascending: false });

      if (regError) throw regError;
      setRegistrations(registrations || []);

      // Bağlantı loglarını yükle
      const { data: logs, error: logsError } = await supabase
        .from('connection_logs')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(100);

      if (logsError) throw logsError;
      setConnectionLogs(logs || []);

      // KVKK metnini yükle
      const { data: settings, error: settingsError } = await supabase
        .from('settings')
        .select('kvkk_text')
        .single();

      if (!settingsError && settings) {
        setKvkkText(settings.kvkk_text || '');
      }

    } catch (error) {
      console.error('Error loading data:', error);
      alert('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKvkkEdit = () => {
    setTempKvkkText(kvkkText);
    setIsEditingKvkk(true);
  };

  const handleKvkkSave = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      const supabase = createClient(supabaseUrl, supabaseKey);

      const { error } = await supabase
        .from('settings')
        .upsert([
          {
            key: 'kvkk_text',
            value: tempKvkkText,
            updated_at: new Date().toISOString()
          }
        ]);

      if (error) {
        throw error;
      }

      setKvkkText(tempKvkkText);
      setIsEditingKvkk(false);
      alert('KVKK metni başarıyla güncellendi.');
    } catch (error) {
      console.error('Error saving KVKK text:', error);
      alert(error instanceof Error ? error.message : 'KVKK metni güncellenirken bir hata oluştu.');
    }
  };

  const handleKvkkCancel = () => {
    setTempKvkkText('');
    setIsEditingKvkk(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const exportData = () => {
    const csvContent = [
      ['Ad', 'Soyad', 'E-posta', 'Telefon', 'Kayıt Tarihi', 'KVKK Onay Tarihi', 'IP Adresi'],
      ...registrations.map(reg => [
        reg.name,
        reg.surname,
        reg.email,
        reg.phone,
        formatDate(reg.registration_date),
        formatDate(reg.kvkk_accepted_at),
        reg.ip_address
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rsvp_kayitlari_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_authenticated');
    setIsAuthenticated(false);
  };

  // Yükleme durumu
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-indigo-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Giriş yapılmamışsa login ekranını göster
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <i className="ri-dashboard-3-line text-2xl text-indigo-600"></i>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">Admin Paneli</h1>
                <p className="text-sm text-gray-500">RSVP Yönetim Sistemi</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('registrations')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'registrations'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-user-line mr-2"></i>
              Kayıtlar ({registrations.length})
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'logs'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-file-list-line mr-2"></i>
              Bağlantı Logları ({connectionLogs.length})
            </button>
            <button
              onClick={() => setActiveTab('kvkk')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'kvkk'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-shield-check-line mr-2"></i>
              KVKK Metni
            </button>
          </nav>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-2xl text-indigo-600 mr-3"></i>
            <span className="text-gray-600">Veriler yükleniyor...</span>
          </div>
        ) : (
          <>
            {/* Registrations Tab */}
            {activeTab === 'registrations' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Kayıtlı Katılımcılar</h2>
                  <p className="text-sm text-gray-600 mt-1">Toplam {registrations.length} kayıt</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Katılımcı
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          E-posta
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Telefon
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Kayıt Tarihi
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Katılım Durumu
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP Adresi
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {registrations.map((registration) => (
                        <tr key={registration.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {registration.name} {registration.surname}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{registration.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{registration.phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(registration.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {registration.participation ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <i className="ri-check-line mr-1"></i>
                                Evet, katılacağım
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <i className="ri-close-line mr-1"></i>
                                Hayır, katılmayacağım
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {registration.ip_address || 'Bilinmiyor'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Connection Logs Tab */}
            {activeTab === 'logs' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Bağlantı Logları</h2>
                  <p className="text-sm text-gray-600 mt-1">Toplam {connectionLogs.length} bağlantı</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP Adresi
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tarih/Saat
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sayfa
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tarayıcı
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {connectionLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{log.ip_address}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(log.timestamp)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{log.page_url}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-500 max-w-xs truncate" title={log.user_agent}>
                              {log.user_agent}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* KVKK Tab */}
            {activeTab === 'kvkk' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">KVKK Metni Yönetimi</h2>
                      <p className="text-sm text-gray-600 mt-1">Kullanıcılara gösterilen KVKK metnini düzenleyin</p>
                    </div>
                    {!isEditingKvkk && (
                      <button
                        onClick={handleKvkkEdit}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-edit-line mr-2"></i>
                        Düzenle
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  {isEditingKvkk ? (
                    <div className="space-y-4">
                      <textarea
                        value={tempKvkkText}
                        onChange={(e) => setTempKvkkText(e.target.value)}
                        rows={15}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                        placeholder="KVKK metnini buraya yazınız..."
                      />
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={handleKvkkSave}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-save-line mr-2"></i>
                          Kaydet
                        </button>
                        <button
                          onClick={handleKvkkCancel}
                          className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-close-line mr-2"></i>
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                        {kvkkText}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
